import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TriggerFieldInputs from "../Workflows/TriggerFieldInputs";
import { parseAllowedValues } from "../Workflows/parseAllowedValues";

const copper = "#B87333";

// Seed inline-input state from a run card's inputs. Shared by the initial
// useState initializer AND the in-place re-preview reset effect so both stay
// DRY and identical.
function initialInputValues(inputs) {
  const init = {};
  for (const inp of inputs || []) {
    if (inp.widget !== "conversational" && inp.value != null) {
      init[inp.key] = inp.value;
    } else if (inp.widget !== "conversational") {
      init[inp.key] = "";
    }
  }
  return init;
}

/**
 * CasperRunCard — renders a run card with inline-editable typed inputs,
 * match confidence chip, alternatives ladder, and Run action.
 *
 * Composes the shared <TriggerFieldInputs> per input field — does NOT
 * re-implement per-type widgets.
 */
export default function CasperRunCard({
  runCard,
  confidence,
  loading = false,
  previewToken,
  onRun,
  onPickAlternative,
  onBuildFresh,
  onBuildWorkflow,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Input state: initialize from runCard.inputs values
  const [inputValues, setInputValues] = useState(() => initialInputValues(runCard?.inputs));

  const [showAlternatives, setShowAlternatives] = useState(false);
  // Selection identity is the alternative's unique `id` (decoupled from
  // resource.id), so clicking one entry never highlights another that happens
  // to share a resource id.
  const [selectedAltId, setSelectedAltId] = useState(null);

  // A re-preview (alt-pick / build-fresh / build-workflow) replaces this
  // message's run card IN PLACE while the component stays mounted — the fresh
  // preview mints a new preview_id. Re-seed the input values and clear any
  // stale selection/toggle so B's card never shows A's edits or selection.
  const previewId = runCard?.preview_lock?.preview_id;
  useEffect(() => {
    setInputValues(initialInputValues(runCard?.inputs));
    setSelectedAltId(null);
    setShowAlternatives(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewId]);

  // Determine which inputs to render inline (exclude conversational)
  const inlineInputs = useMemo(
    () => (runCard?.inputs || []).filter((inp) => inp.widget !== "conversational"),
    [runCard?.inputs]
  );
  const conversationalInputs = useMemo(
    () => (runCard?.inputs || []).filter((inp) => inp.widget === "conversational"),
    [runCard?.inputs]
  );

  // Run button disabled: any required non-conversational input is empty
  const runDisabled = useMemo(() => {
    for (const inp of inlineInputs) {
      if (inp.required) {
        const v = inputValues[inp.key];
        if (v === "" || v === null || v === undefined) return true;
      }
    }
    return false;
  }, [inlineInputs, inputValues]);

  const handleRun = () => {
    if (!onRun) return;
    // Build payload: typed values, omit conversational keys
    const payload = {};
    for (const inp of inlineInputs) {
      let val = inputValues[inp.key];
      // Preserve numeric types
      if ((inp.type === "number" || inp.type === "integer") && val !== "" && val != null) {
        val = Number(val);
      }
      if (val !== "" && val != null) {
        payload[inp.key] = val;
      } else if (inp.value != null && inp.value !== "") {
        payload[inp.key] = inp.value;
      }
    }
    if (previewToken) {
      payload.preview_token = previewToken;
    }
    onRun(payload);
  };

  const matchPercent = confidence != null ? `${Math.round(confidence * 100)}%` : null;

  // R2-1: for a freshly composed/generated workflow the identity lives in
  // `plan` (name + step summary), not `resource`. Show the plan name as the
  // heading and a compact step strip above the typed inputs.
  const plan = runCard?.plan;
  const planSteps = plan?.steps || [];
  const headingLabel = plan?.name
    ? plan.name
    : `Use ${runCard?.resource?.name || "workflow"}`;

  // §4a-2 "Both on one card": a first-class Build-a-workflow secondary offered
  // on a strong single-agent card when the intent carries multi-deliverable
  // cues. Rendered in the primary action area — NOT inside the "Not this?"
  // alternatives ladder — so it stays visible when the ladder is collapsed.
  const buildWorkflowAction = (runCard?.secondary_actions || []).find(
    (a) => a?.kind === "build_workflow"
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${alpha(copper, 0.3)}`,
        bgcolor: isDark ? alpha(copper, 0.06) : alpha(copper, 0.04),
      }}
    >
      {/* Action line */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: copper }}>
          {headingLabel}
        </Typography>
        {matchPercent && (
          <Chip
            label={matchPercent}
            size="small"
            sx={{
              bgcolor: alpha(copper, 0.12),
              color: copper,
              fontWeight: 600,
              fontSize: "0.7rem",
            }}
          />
        )}
      </Stack>

      {/* Workflow step strip (new_agentic_workflow only) — compact chips + arrows,
          above the typed inputs. Human-approval gate and branch steps are marked. */}
      {planSteps.length > 0 && (
        <Box
          data-testid="casper-plan-strip"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 0.5,
            mb: 1.5,
          }}
        >
          {planSteps.map((step, idx) => {
            const isGate = step.gate === "human_approval";
            const isBranch = Boolean(step.branch);
            const marker = isGate ? "⏸ " : isBranch ? "⑂ " : "";
            return (
              <React.Fragment key={step.id || idx}>
                {idx > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    aria-hidden="true"
                    sx={{ color: "text.disabled", mx: 0.25 }}
                  >
                    →
                  </Typography>
                )}
                <Chip
                  size="small"
                  label={`${marker}${step.label || step.id}`}
                  title={step.agent_display_name || step.label || step.id}
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: isGate || isBranch ? 600 : 500,
                    color: isGate || isBranch ? copper : "text.secondary",
                    bgcolor: isGate
                      ? alpha(copper, 0.16)
                      : isBranch
                        ? alpha(copper, 0.1)
                        : alpha(copper, 0.06),
                    border: `1px solid ${alpha(copper, 0.2)}`,
                  }}
                />
              </React.Fragment>
            );
          })}
        </Box>
      )}

      {/* Inline inputs */}
      <Box sx={{ mb: 1 }}>
        {inlineInputs.map((inp) => {
          const fieldDef = {
            name: inp.label || inp.key,
            type: inp.type || "string",
            description: inp.description,
            required: inp.required,
          };
          const options = inp.options || parseAllowedValues(inp.description);
          const isMissing = inp.required && (inputValues[inp.key] === "" || inputValues[inp.key] == null);
          return (
            <TriggerFieldInputs
              key={inp.key}
              field={fieldDef}
              value={inputValues[inp.key] ?? ""}
              onChange={(v) => setInputValues((prev) => ({ ...prev, [inp.key]: v }))}
              error={isMissing}
              options={options}
            />
          );
        })}
      </Box>

      {/* Conversational fallback notes */}
      {conversationalInputs.map((inp) => (
        <Typography
          key={inp.key}
          variant="caption"
          sx={{ display: "block", mb: 0.5, color: "text.secondary", fontStyle: "italic" }}
        >
          I'll ask about {inp.label || inp.key} in chat
        </Typography>
      ))}

      {/* Action buttons */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<PlayArrowIcon />}
          disabled={runDisabled || loading}
          onClick={handleRun}
          sx={{
            bgcolor: copper,
            "&:hover": { bgcolor: alpha(copper, 0.85) },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Run
        </Button>
        {buildWorkflowAction && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            data-testid="casper-build-workflow"
            disabled={loading}
            onClick={() => onBuildWorkflow && onBuildWorkflow()}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: copper,
              borderColor: alpha(copper, 0.5),
              "&:hover": { borderColor: copper, bgcolor: alpha(copper, 0.06) },
            }}
          >
            {buildWorkflowAction.label || "Build a workflow instead"}
          </Button>
        )}
        {runCard?.alternatives?.length > 0 && (
          <Button
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={() => setShowAlternatives((v) => !v)}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Not this?
          </Button>
        )}
      </Stack>

      {/* Alternatives ladder */}
      <Collapse in={showAlternatives}>
        <Stack spacing={0.5} sx={{ mt: 1.5, pl: 1 }}>
          {(runCard?.alternatives || []).map((alt, idx) => {
            const altId = alt.id ?? idx;
            const isSelected = selectedAltId === altId;
            return (
              <Button
                key={altId}
                data-testid={`casper-alt-${altId}`}
                aria-pressed={isSelected}
                size="small"
                disabled={loading}
                variant={isSelected ? "outlined" : "text"}
                onClick={() => {
                  setSelectedAltId(altId);
                  if (onPickAlternative) onPickAlternative(alt.resource);
                }}
                sx={{
                  textTransform: "none",
                  justifyContent: "flex-start",
                  color: "text.primary",
                  ...(isSelected ? { borderColor: copper } : {}),
                }}
              >
                {alt.resource?.name || `Alternative ${idx + 1}`}
                {alt.match != null && (
                  <Chip label={`${Math.round(alt.match * 100)}%`} size="small" sx={{ ml: 1, fontSize: "0.65rem" }} />
                )}
              </Button>
            );
          })}
          <Button
            size="small"
            variant="text"
            disabled={loading}
            onClick={() => onBuildFresh && onBuildFresh()}
            sx={{ textTransform: "none", justifyContent: "flex-start", fontWeight: 600, color: copper }}
          >
            Build fresh
          </Button>
        </Stack>
      </Collapse>
    </Paper>
  );
}
